import { z } from "zod";
import { Action, Subject } from "@erp/authz";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Verify Step Output
 *
 * A generic AI "overseer" step: it sanity-checks the object a previous step
 * produced (its `subject`), optionally against the input that produced it
 * (`priorInput`), using author-written `criteria`. When the check fails, it
 * escalates for human review by opening a task in the inbox — so a bad
 * extraction (garbled amounts, a total that doesn't match its line items, an
 * implausible customer) is caught and handed to a person instead of flowing
 * downstream unnoticed.
 *
 * It is deliberately domain-agnostic: wire any upstream step's output into
 * `subject` and (optionally) that step's input into `priorInput`. Remittance
 * normalization is the first consumer, but AP invoices, OMS drafts, etc. can
 * reuse the same node.
 */

export const VERIFY_MODEL_TIERS = ["fast", "deep_thinking"] as const;
export type VerifyModelTier = (typeof VERIFY_MODEL_TIERS)[number];

export const VERIFY_VERDICTS = ["pass", "needs_review"] as const;
export type VerifyVerdict = (typeof VERIFY_VERDICTS)[number];

export const VERIFY_FINDING_SEVERITIES = [
  "info",
  "warning",
  "critical",
] as const;
export type VerifyFindingSeverity = (typeof VERIFY_FINDING_SEVERITIES)[number];

export const VERIFY_TASK_PRIORITIES = [
  "low",
  "medium",
  "high",
  "urgent",
] as const;

export const verifyStepOutputStep = defineStepConfig({
  type: "verify_step_output",
  name: "Verify Step Output",
  description:
    "AI sanity-check over a previous step's output (and optionally its input) against configurable criteria. On a failed check, escalates for human review by opening a task in the inbox.",
  category: "AI & Agents",

  inputSchema: z.object({
    subject: z
      .record(z.string(), z.unknown())
      .describe(
        "The object to sanity-check — typically the previous step's output.",
      ),
    priorInput: z
      .record(z.string(), z.unknown())
      .optional()
      .describe(
        "The input that produced `subject`, supplied for context so the check can reason about the transformation.",
      ),
    documentIds: z
      .array(z.string().uuid())
      .optional()
      .describe(
        "Optional source document ids (CoreDocument). When set, the documents (PDF/image) are attached to the model call so the check can verify `subject` against the ORIGINAL source, not just its extracted JSON.",
      ),
    sourceEntityType: z
      .nativeEnum(SystemEntity)
      .optional()
      .describe(
        "Entity type to link the escalation task to (paired with sourceEntityId), e.g. the document or payment under review.",
      ),
    sourceEntityId: z
      .string()
      .uuid()
      .optional()
      .describe("Entity id to link the escalation task to."),
  }),

  outputSchema: z.object({
    verdict: z
      .enum(VERIFY_VERDICTS)
      .describe("`pass` when the output looks sound, else `needs_review`."),
    summary: z.string().describe("One-line summary of the check outcome."),
    findings: z
      .array(
        z.object({
          severity: z.enum(VERIFY_FINDING_SEVERITIES),
          title: z.string(),
          detail: z.string(),
          reference: z
            .string()
            .nullable()
            .describe(
              "Pointer to the specific item this finding is about (e.g. an invoice ref or line-item id) so a consumer can highlight it; null when the finding isn't item-specific.",
            ),
        }),
      )
      .describe("Concrete problems the check surfaced (empty on pass)."),
    taskId: z
      .string()
      .nullable()
      .describe("Id of the escalation task opened for review, or null."),
  }),

  configSchema: z
    .object({
      criteria: z
        .string()
        .min(1)
        .max(10_000)
        .describe(
          "What the check should verify, in plain language — e.g. 'The payment total must equal the sum of the line-item amounts; customer name and invoice numbers must look like real values, not OCR garble.'",
        ),
      modelTier: z
        .enum(VERIFY_MODEL_TIERS)
        .default("fast")
        .describe("Which model tier runs the check."),
      escalationPriority: z
        .enum(VERIFY_TASK_PRIORITIES)
        .default("high")
        .describe("Priority of the review task opened on a failed check."),
      taskTitle: z
        .string()
        .max(512)
        .optional()
        .describe(
          "Title for the escalation task. Defaults to a generated title referencing the check.",
        ),
      assigneeType: z
        .enum(["user", "agent"])
        .optional()
        .describe("Who the escalation task is assigned to."),
      assigneeUserId: z
        .string()
        .uuid()
        .optional()
        .describe("User id when assigneeType is 'user'."),
      assigneeAgentDefinitionId: z
        .string()
        .uuid()
        .optional()
        .describe("Agent definition id when assigneeType is 'agent'."),
    })
    .superRefine((value, ctx) => {
      if (value.assigneeType === "user" && !value.assigneeUserId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assigneeUserId"],
          message: "assigneeUserId is required when assigneeType is 'user'",
        });
      }
      if (value.assigneeType === "user" && value.assigneeAgentDefinitionId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assigneeAgentDefinitionId"],
          message:
            "assigneeAgentDefinitionId must not be set when assigneeType is 'user'",
        });
      }
      if (value.assigneeType === "agent" && !value.assigneeAgentDefinitionId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assigneeAgentDefinitionId"],
          message:
            "assigneeAgentDefinitionId is required when assigneeType is 'agent'",
        });
      }
      if (value.assigneeType === "agent" && value.assigneeUserId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assigneeUserId"],
          message:
            "assigneeUserId must not be set when assigneeType is 'agent'",
        });
      }
      if (
        !value.assigneeType &&
        (value.assigneeUserId || value.assigneeAgentDefinitionId)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assigneeType"],
          message:
            "assigneeType is required when a default assignee id is provided",
        });
      }
    }),

  emits: [] as const,

  requiredPermissions: [{ action: Action.Create, subject: Subject.Task }],
});
